"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = MyBenefitsCard;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const cancelable_promise_1 = require("cancelable-promise");
const react_native_1 = require("react-native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const react_i18next_1 = require("react-i18next");
const Theme_1 = require("../Theme");
const Benefits_1 = __importDefault(require("../api/Benefits"));
const UserContext_1 = require("../contexts/UserContext");
const Card_1 = __importDefault(require("../components/Card"));
const MainMenuButton_1 = __importDefault(require("../components/MainMenuButton"));
const MainMenuItem_1 = __importDefault(require("../components/MainMenuItem"));
const Loading_1 = __importDefault(require("../components/Loading"));
const ThemeContext_1 = require("../ThemeContext");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const FadeInView_1 = __importDefault(require("../components/FadeInView"));
const MaintenanceContext_1 = require("../contexts/MaintenanceContext");
const useStyles = () => {
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    return react_native_1.StyleSheet.create({
        buttonIcon: {
            color: THEME.Secondary,
            marginRight: 6
        },
        buttonText: {
            color: THEME.Text,
            fontSize: size != 'sm' ? 20 : 18,
            marginTop: -1,
            textAlign: 'center',
            userSelect: 'none'
        }
    });
};
function MyBenefitsCard(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const styles = useStyles();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const { maintenanceMode, maintenanceCheck } = (0, MaintenanceContext_1.useMaintenanceContext)();
    const promise = react_1.default.useRef(null);
    const fsaPromise = react_1.default.useRef(null);
    const hraPromise = react_1.default.useRef(null);
    const lifeEventsPromise = react_1.default.useRef(null);
    const creditBackPromise = react_1.default.useRef(null);
    const [benefits, setBenefits] = react_1.default.useState(null);
    const [moreCount, setMoreCount] = react_1.default.useState(0);
    const [status, setStatus] = react_1.default.useState(null);
    const [shouldShowForFormer, setShouldShowForFormer] = react_1.default.useState(true);
    const [hasSponsoredPlan, setHasSponsoredPlan] = react_1.default.useState(false);
    const [reimbursementArrangement, setReimbursementArrangement] = react_1.default.useState(null);
    const [hasPendingLifeEvent, setHasPendingLifeEvent] = react_1.default.useState(false);
    const [isCreditBack, setIsCreditBack] = react_1.default.useState(false);
    const limitedView = userContext.isFormer || userContext.isContractor;
    const refresh = react_1.default.useCallback(async () => {
        if (!maintenanceCheck || maintenanceMode !== "off")
            return;
        promise.current?.cancel();
        fsaPromise.current?.cancel();
        hraPromise.current?.cancel();
        lifeEventsPromise.current?.cancel();
        creditBackPromise.current?.cancel();
        setStatus('loading');
        setBenefits(null);
        // Load only spending accounts and HRA for former employees/contractors
        if (limitedView) {
            fsaPromise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => Benefits_1.default.getSpendingAccounts(token))).then(saData => {
                let totalCount = 0;
                setHasSponsoredPlan(saData?.hasSponsoredPlan);
                if ((saData?.spendingAccounts?.length > 0 || saData?.hasSponsoredPlan) && saData?.isActiveOrRecentlyTerminated) {
                    totalCount++;
                    setMoreCount(totalCount);
                    setShouldShowForFormer(totalCount > 0);
                }
                hraPromise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => Benefits_1.default.getReimbursementArrangement(token))).then(hraData => {
                    if ((hraData?.description || hraData?.hasHadSponsoredPlan) && saData?.isActiveOrRecentlyTerminated) {
                        totalCount++;
                        setReimbursementArrangement(hraData);
                    }
                    setMoreCount(totalCount);
                    setShouldShowForFormer(totalCount > 0);
                }).catch(error => {
                    setStatus('error');
                    console.error(error);
                });
            }).catch(error => {
                setStatus('error');
                console.error(error);
            });
            setStatus('done');
        }
        else {
            promise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => Benefits_1.default.getList(token))).then(data => {
                const benefits = data?.benefits ?? [];
                let totalCount = benefits?.filter((p) => !['Medical', 'Dental', 'Vision'].includes(p.category)).length;
                const eligible = data?.isEmploymentTypeEligible === true;
                if (eligible) {
                    totalCount += 1;
                    if (!userContext.isInternal)
                        totalCount += 1;
                }
                fsaPromise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => Benefits_1.default.getSpendingAccounts(token))).then(saData => {
                    if ((saData?.spendingAccounts?.length > 0 || saData?.hasSponsoredPlan) && saData?.isActiveOrRecentlyTerminated) {
                        totalCount++;
                        setMoreCount(totalCount);
                    }
                    hraPromise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => Benefits_1.default.getReimbursementArrangement(token))).then(hraData => {
                        if ((hraData?.description || hraData?.hasHadSponsoredPlan) && saData?.isActiveOrRecentlyTerminated) {
                            totalCount++;
                        }
                        setMoreCount(totalCount);
                        // Check if client is credit back (doesn't have Nextep benefits), if not, check for pending life events
                        creditBackPromise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => Benefits_1.default.isCreditBack(token))).then((response) => {
                            setIsCreditBack(!!response.isCreditBack);
                            if (response.isCreditBack == false) {
                                lifeEventsPromise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => Benefits_1.default.getPendingLifeEvent(token))).then((leData) => {
                                    setHasPendingLifeEvent(!!leData);
                                }).catch(error => {
                                    setStatus(error == 204 ? 'done' : 'error');
                                });
                            }
                            setBenefits({
                                medical: data.benefits.filter((p) => p.category == 'Medical')[0],
                                dental: data.benefits.filter((p) => p.category == 'Dental')[0],
                                vision: data.benefits.filter((p) => p.category == 'Vision')[0],
                            });
                            setStatus('done');
                        }).catch(error => {
                            setStatus('error');
                            console.error(error);
                        });
                    }).catch(error => {
                        setStatus('error');
                        console.error(error);
                    });
                }).catch(error => {
                    setStatus('error');
                    console.error(error);
                });
            }).catch((e) => {
                setStatus('error');
                console.error(e);
            });
        }
    }, [maintenanceMode, maintenanceCheck]);
    react_1.default.useEffect(() => {
        refresh();
        return () => {
            promise.current?.cancel();
            fsaPromise.current?.cancel();
            hraPromise.current?.cancel();
            lifeEventsPromise.current?.cancel();
            creditBackPromise.current?.cancel();
            react_native_1.DeviceEventEmitter.removeAllListeners('event.lifeEventUpdate');
        };
    }, [refresh]);
    const BenefitsTiles = () => react_1.default.useMemo(() => ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', flexWrap: 'wrap' }, children: [benefits?.medical &&
                (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { width: '50%' }, children: (0, jsx_runtime_1.jsxs)(MainMenuButton_1.default, { "aria-label": t('myBenefits:labels.medical'), accessibilityHint: t('myBenefits:hints.medical'), style: { margin: 3 }, onPress: () => props.navigation.navigate('BenefitPopupScreen', { data: benefits['medical'], benefit: 'medical', view: 'detail' }), children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.HeartPulse, size: 24, style: [styles.buttonIcon] }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { accessible: false, style: styles.buttonText, children: t('myBenefits:labels.medical') })] }) }), benefits?.dental &&
                (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { width: '50%' }, children: (0, jsx_runtime_1.jsxs)(MainMenuButton_1.default, { "aria-label": t('myBenefits:labels.dental'), accessibilityHint: t('myBenefits:hints.dental'), style: { margin: 3 }, onPress: () => props.navigation.navigate('BenefitPopupScreen', { data: benefits['dental'], benefit: 'dental', view: 'detail' }), children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.Tooth, size: 24, style: [styles.buttonIcon] }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { accessible: false, style: styles.buttonText, children: t('myBenefits:labels.dental') })] }) }), benefits?.vision &&
                (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { width: '50%' }, children: (0, jsx_runtime_1.jsxs)(MainMenuButton_1.default, { "aria-label": t('myBenefits:labels.vision'), accessibilityHint: t('myBenefits:hints.vision'), style: { margin: 3 }, onPress: () => props.navigation.navigate('BenefitPopupScreen', { data: benefits['vision'], benefit: 'vision', view: 'detail' }), children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.Glasses, size: 24, style: [styles.buttonIcon] }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { accessible: false, style: styles.buttonText, children: t('myBenefits:labels.vision') })] }) }), (moreCount > 0 && (benefits?.medical ||
                benefits?.dental ||
                benefits?.vision)) &&
                (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { width: '50%' }, children: (0, jsx_runtime_1.jsxs)(MainMenuButton_1.default, { "aria-label": '+ ' + (moreCount).toString() + t('myBenefits:labels.more'), accessibilityHint: t('myBenefits:hints.all'), onPress: () => props.navigation.navigate('MyBenefits'), pressedStyle: { opacity: 0.5 }, style: {
                            backgroundColor: size != 'sm' ? THEME.UltraLightGray : THEME.SoftGray,
                            borderColor: size != 'sm' ? THEME.UltraLightGray : THEME.SoftGray,
                            margin: 3
                        }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.BriefcaseMedical, size: 24, style: { color: THEME.PrimaryDark, marginRight: 6 } }), (0, jsx_runtime_1.jsxs)(react_native_1.Text, { accessible: false, style: { textAlign: 'center', fontSize: size != 'sm' ? 20 : 18, fontWeight: '500', color: THEME.PrimaryDark, userSelect: 'none' }, children: [moreCount, t('myBenefits:labels.more')] })] }) })] })), [benefits, moreCount]);
    const handleLifeEventPress = () => {
        if (!hasPendingLifeEvent) {
            react_native_1.DeviceEventEmitter.addListener('event.lifeEventUpdate', () => refresh());
        }
        props.navigation.navigate(hasPendingLifeEvent ? 'MyBenefits' : 'AddLifeEvent');
    };
    return (((limitedView ? limitedView && shouldShowForFormer : true) && !(limitedView && !hasSponsoredPlan && reimbursementArrangement == null)) &&
        (0, jsx_runtime_1.jsx)(Card_1.default, { style: {
                backgroundColor: 'transparent',
                borderWidth: 0,
                borderBottomWidth: 0,
                padding: 0,
                marginTop: 12
            }, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { margin: -3 }, children: [(0, jsx_runtime_1.jsx)(MainMenuItem_1.default, { disabled: (limitedView && !shouldShowForFormer) || (maintenanceCheck && maintenanceMode !== "off"), icon: Theme_1.ICONS.Solid.BriefcaseMedical, text: t('common:screenTitles.benefits'), style: { margin: 3, zIndex: 2 }, onPress: () => props.navigation.navigate('MyBenefits') }), (status == 'done') &&
                        (0, jsx_runtime_1.jsxs)(FadeInView_1.default, { children: [(benefits?.medical || benefits?.dental || benefits?.vision || moreCount > 0) &&
                                    maintenanceMode === "off" &&
                                    (0, jsx_runtime_1.jsx)(BenefitsTiles, {}), (!limitedView && !isCreditBack && maintenanceMode === "off") &&
                                    (0, jsx_runtime_1.jsxs)(MainMenuButton_1.default, { "aria-label": hasPendingLifeEvent ? t('myBenefits:text.pendingLifeEvent') : t('myBenefits:text.reportLifeEvent'), style: { margin: 3 }, onPress: handleLifeEventPress, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.FileMedical, size: 20, style: [styles.buttonIcon] }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { accessible: false, style: [styles.buttonText, { fontSize: size != 'sm' ? 18 : 16, userSelect: 'none' }], children: hasPendingLifeEvent ? t('myBenefits:text.pendingLifeEvent') : t('myBenefits:text.reportLifeEvent') })] })] })
                        ||
                            (0, jsx_runtime_1.jsx)(Loading_1.default, { loading: status == 'loading' })] }) })
        || (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {}));
}
;
